import { useEffect, useRef } from "react";

import { useMutation } from "react-query";

import * as apiTypes from "../types/api";
import * as contexts from "../types/contexts";
import { api, misc } from "../utils";

import { useClientAttributes } from "./useClientAttributes";
import { useLocalStorageItem } from "./useLocalStorageItem";

export interface ISessionInitializationResult {
    session: contexts.ISessionContext | null;
    expired: boolean;
    isLoading: boolean;
}

export const useInitializeSession = (): ISessionInitializationResult => {
    const attributes = useClientAttributes();
    const [session, setSession] =
        useLocalStorageItem<contexts.ISessionContext | null>(
            "session",
            null,
            JSON.stringify,
            JSON.parse,
        );
    const [lastActiveTime, setLastActiveTime] = useLocalStorageItem<number>(
        "lastActiveTime",
        0,
        misc.serializeInteger,
        parseInt,
    );

    const currentTime = new Date().getTime();
    const difference = currentTime - lastActiveTime;
    const expired = Boolean(
        (difference > 1000 * 60 * 30 || !session) && attributes,
    );

    const { mutate, isLoading, isError, isSuccess } = useMutation({
        mutationFn: (attributes0: apiTypes.IClientAttributes) =>
            api.v1_1.sessions.createSession(attributes0),
        onSuccess: (session0: contexts.ISessionContext) => {
            setSession(session0);

            /* Currently, the last active time is either expired or sentinel.
             * Therefore, update it to trigger the side effect that creates a
             * timer.
             */
            const newLastActiveTime = new Date().getTime();
            setLastActiveTime(newLastActiveTime);
        },
    });

    /* A ref that maintains a handle to the timer. This allows us to prevent
     * creation of multiple timers.
     */
    const intervalRef = useRef<NodeJS.Timer>();

    /* The side effect implemented below creates a timer that updates the last
     * active time every minute.
     * Conditions that need to uphold for the effect to work:
     *  - The last active time is not 0, that is, the sentinel value that is
     *    used to indicate unknown last active time.
     *  - A timer was not previously created.
     *  - An active session exists.
     */
    useEffect(() => {
        let mounted = true;

        if (
            lastActiveTime > 0 &&
            attributes &&
            session &&
            !intervalRef.current
        ) {
            intervalRef.current = setInterval(() => {
                if (mounted) {
                    const newLastActiveTime = new Date().getTime();
                    setLastActiveTime(newLastActiveTime);
                }
            }, 1000 * 60);
        }

        return () => {
            clearInterval(intervalRef.current as NodeJS.Timeout);

            mounted = false;
        };
    }, [attributes, lastActiveTime, session, setLastActiveTime]);

    useEffect(() => {
        /* Create a new session if the last activity was more than 30 minutes
         * ago.
         */
        if (attributes && expired && !isLoading && !isSuccess && !isError) {
            mutate(attributes);
        }
    }, [attributes, expired, isError, isLoading, isSuccess, mutate]);

    return { session, expired, isLoading };
};
